import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import FeedbackButton from '@/components/FeedbackButton';
import { 
  Send, 
  Scale, 
  User,
  Bot,
  Loader2,
  HelpCircle,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const AssistantJuridique = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesContainerRef = useRef(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Gestion du retour arrière avec confirmation
  useEffect(() => {
    if (messages.length === 0) return;

    const handlePopState = (e) => {
      e.preventDefault();
      // Empêcher la navigation et afficher le dialog
      window.history.pushState(null, '', location.pathname);
      setShowExitDialog(true);
    };

    // Ajouter une entrée dans l'historique pour intercepter le retour arrière
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [messages.length, location.pathname]);

  // Gestion de la fermeture de page/onglet
  useEffect(() => {
    if (messages.length === 0) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Vos messages seront perdus si vous quittez. Continuer ?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleConfirmExit = () => {
    setShowExitDialog(false);
    setMessages([]);
    setSessionId(null);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      navigate(-1);
    }
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
    setPendingNavigation(null);
  };

  const clearConversation = () => {
    if (messages.length > 0) {
      setShowExitDialog(true);
      setPendingNavigation('clear');
    }
  };

  const handleClearConfirm = () => {
    setShowExitDialog(false);
    setMessages([]);
    setSessionId(null);
    setPendingNavigation(null);
    toast.success('Conversation effacée');
  };

  // Nettoyer le formatage markdown de la réponse
  const cleanMarkdown = (text) => {
    return text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*/g, '')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/```[\s\S]*?```/g, (match) => match.replace(/```\w*\n?/g, '').trim())
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^\d+\)\s+/gm, '• ')
      .replace(/^-\s+/gm, '• ')
      .trim();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    const userMsgObj = {
      id: 'user-' + Date.now(),
      role: 'user',
      content: userMessage
    };
    setMessages(prev => [...prev, userMsgObj]);
    
    setLoading(true);

    try {
      const response = await apiClient.post('/assistant/chat', {
        message: userMessage,
        session_id: sessionId
      });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const cleanedResponse = cleanMarkdown(response.data.response);

      const assistantMsg = {
        id: response.data.message_id,
        role: 'assistant',
        content: cleanedResponse,
        fullContent: cleanedResponse,
        displayedContent: '',
        isTyping: true
      };

      setMessages(prev => [...prev, assistantMsg]);

    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur de communication avec l\'assistant';
      
      if (errorMsg.includes('Budget') || errorMsg.includes('exceeded')) {
        toast.error('Budget IA dépassé. Veuillez recharger via Profil → Universal Key.');
      } else {
        toast.error(errorMsg);
      }
      
      setMessages(prev => prev.filter(m => m.id !== userMsgObj.id));
    } finally {
      setLoading(false);
    }
  };

  // Composant pour l'effet de typing progressif
  const TypewriterText = ({ message, onComplete }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const fullText = message.fullContent || message.content;

    useEffect(() => {
      if (!message.isTyping) {
        setDisplayedText(fullText);
        return;
      }

      if (currentIndex < fullText.length) {
        const chunkSize = Math.min(5, fullText.length - currentIndex);
        const timeout = setTimeout(() => {
          setDisplayedText(prev => prev + fullText.slice(currentIndex, currentIndex + chunkSize));
          setCurrentIndex(prev => prev + chunkSize);
        }, 15);
        return () => clearTimeout(timeout);
      } else if (message.isTyping && onComplete) {
        onComplete(message.id);
      }
    }, [currentIndex, fullText, message.isTyping, message.id, onComplete]);

    return <span className="whitespace-pre-wrap">{displayedText}</span>;
  };

  const handleTypingComplete = useCallback((messageId) => {
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, isTyping: false } : m
    ));
  }, []);

  const suggestedQuestions = [
    { icon: Scale, text: "Quelles sont les règles de majorité en AG ?" },
    { icon: HelpCircle, text: "Comment créer une assemblée générale ?" },
    { icon: Scale, text: "Comment gérer un copropriétaire qui ne paie pas ?" },
    { icon: HelpCircle, text: "Comment fonctionne l'abonnement Premium ?" }
  ];

  return (
    <>
      <div className="flex flex-col h-[calc(100vh-180px)] sm:h-[calc(100vh-120px)] bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b bg-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Scale className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900 text-sm sm:text-base">Assistant Copropriété</h2>
              <p className="text-xs text-slate-500 hidden sm:block">Aide app + conseils juridiques</p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearConversation}
              className="text-slate-500 hover:text-red-500 text-xs sm:text-sm"
            >
              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Effacer</span>
            </Button>
          )}
        </div>

        {/* Messages - Zone scrollable */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-3 sm:space-y-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {messages.length === 0 ? (
            <div className="min-h-full flex flex-col items-center justify-center text-center px-2 sm:px-4 py-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                <Scale className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                Assistant Copropriété
              </h3>
              <p className="text-slate-500 mb-4 sm:mb-6 max-w-md text-sm sm:text-base">
                Je peux vous aider sur l'utilisation de l'application et 
                répondre à vos questions juridiques sur la copropriété.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q.text)}
                    className="text-left p-2 sm:p-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs sm:text-sm text-slate-700 transition-colors border border-slate-200 flex items-start gap-2"
                  >
                    <q.icon className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-slate-400 mt-4 sm:mt-6 max-w-md">
                Les conseils juridiques sont informatifs et ne remplacent pas l'avis d'un professionnel.
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 sm:gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[80%] p-3 sm:p-4 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-slate-900 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-900 rounded-bl-md'
                  }`}
                >
                  <p className="text-xs sm:text-sm">
                    {msg.role === 'assistant' && msg.isTyping ? (
                      <TypewriterText message={msg} onComplete={handleTypingComplete} />
                    ) : (
                      <span className="whitespace-pre-wrap">{msg.content}</span>
                    )}
                  </p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-slate-600" />
                  </div>
                )}
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
              </div>
              <div className="bg-slate-100 p-3 sm:p-4 rounded-2xl rounded-bl-md">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-emerald-600" />
                  <span className="text-xs sm:text-sm text-slate-500">Réflexion...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input - Fixé en bas */}
        <div className="p-3 sm:p-4 border-t bg-white flex-shrink-0">
          <form onSubmit={sendMessage} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question..."
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm sm:text-base"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-emerald-600 hover:bg-emerald-700 px-3 sm:px-4"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </form>
          
          {/* Feedback Button */}
          <div className="flex justify-center mt-3">
            <FeedbackButton source="assistant" variant="assistant" />
          </div>
        </div>
      </div>

      {/* Dialog de confirmation de sortie */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Attention
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Votre conversation sera <strong>définitivement effacée</strong> si vous quittez cette page.
              <br /><br />
              Les messages ne sont pas sauvegardés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleCancelExit}
              className="flex-1"
            >
              Rester
            </Button>
            <Button
              onClick={pendingNavigation === 'clear' ? handleClearConfirm : handleConfirmExit}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            >
              {pendingNavigation === 'clear' ? 'Effacer' : 'Quitter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssistantJuridique;
